/**
 * Edit language page: select a language and manage grammatical feature
 * and grammeme priorities for that language (universal user).
 * Language name/description are i18n-only and not editable here.
 */
import { LitElement, html, css, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { gql } from '@apollo/client'
import { apolloClient } from '../utils/apollo.js'
import { t } from '../utils/i18n.js'
import '@material/web/button/filled-button.js'
import '@material/web/select/filled-select.js'
import '@material/web/select/select-option.js'
import styles from './edit-language-page.css?inline'

const LANGUAGES_QUERY = gql`
  query LanguagesForEdit {
    languages {
      id
      name
      description
    }
  }
`

const PRIORITIES_QUERY = gql`
  query PrioritiesForEdit {
    priorities {
      id
      name
    }
  }
`

const LGFP_QUERY = gql`
  query LanguageGrammaticalFeaturePrioritiesForEdit {
    languageGrammaticalFeaturePriorities {
      id
      language { id }
      feature { id name }
      priority { id name }
      deletedAt
    }
  }
`

const LGP_QUERY = gql`
  query LanguageGrammemePrioritiesForEdit {
    languageGrammemePriorities {
      id
      language { id }
      grammeme { id value feature { id name } }
      priority { id name }
      deletedAt
    }
  }
`

const GRAMMATICAL_FEATURES_QUERY = gql`
  query GrammaticalFeaturesForEdit {
    grammaticalFeatures {
      id
      name
    }
  }
`

const GRAMMEMES_QUERY = gql`
  query GrammemesForEdit {
    grammemes {
      id
      value
      feature { id name }
    }
  }
`

const CREATE_LGFP_MUTATION = gql`
  mutation CreateLGFP($input: CreateLanguageGrammaticalFeaturePriorityInput!) {
    createLanguageGrammaticalFeaturePriority(input: $input) {
      id
      language { id }
      feature { id name }
      priority { id name }
    }
  }
`

const UPDATE_LGFP_MUTATION = gql`
  mutation UpdateLGFP($input: UpdateLanguageGrammaticalFeaturePriorityInput!) {
    updateLanguageGrammaticalFeaturePriority(input: $input) {
      id
      language { id }
      feature { id name }
      priority { id name }
    }
  }
`

const DELETE_LGFP_MUTATION = gql`
  mutation DeleteLGFP($id: ID!) {
    deleteLanguageGrammaticalFeaturePriority(id: $id)
  }
`

const RESTORE_LGFP_MUTATION = gql`
  mutation RestoreLGFP($id: ID!) {
    restoreLanguageGrammaticalFeaturePriority(id: $id) {
      id
      language { id }
      feature { id name }
      priority { id name }
      deletedAt
    }
  }
`

const CREATE_LGP_MUTATION = gql`
  mutation CreateLGP($input: CreateLanguageGrammemePriorityInput!) {
    createLanguageGrammemePriority(input: $input) {
      id
      language { id }
      grammeme { id value feature { id name } }
      priority { id name }
    }
  }
`

const UPDATE_LGP_MUTATION = gql`
  mutation UpdateLGP($input: UpdateLanguageGrammemePriorityInput!) {
    updateLanguageGrammemePriority(input: $input) {
      id
      language { id }
      grammeme { id value feature { id name } }
      priority { id name }
    }
  }
`

const DELETE_LGP_MUTATION = gql`
  mutation DeleteLGP($id: ID!) {
    deleteLanguageGrammemePriority(id: $id)
  }
`

const RESTORE_LGP_MUTATION = gql`
  mutation RestoreLGP($id: ID!) {
    restoreLanguageGrammemePriority(id: $id) {
      id
      language { id }
      grammeme { id value feature { id name } }
      priority { id name }
      deletedAt
    }
  }
`

interface LanguageRow {
  id: string
  name: string
  description?: string | null
}

interface PriorityRow {
  id: string
  name: string
}

interface LGFPRow {
  id: string
  language: { id: string }
  feature: { id: string; name: string }
  priority: { id: string; name: string }
  deletedAt?: string | null
}

interface LGPRow {
  id: string
  language: { id: string }
  grammeme: { id: string; value: string; feature: { id: string; name: string } }
  priority: { id: string; name: string }
  deletedAt?: string | null
}

interface GrammaticalFeatureRow {
  id: string
  name: string
}

interface GrammemeRow {
  id: string
  value: string
  feature: { id: string; name: string }
}

@customElement('edit-language-page')
export class EditLanguagePage extends LitElement {
  static styles = css`${unsafeCSS(styles)}`

  @property({ type: String }) initialLanguageId = ''

  @state() private languages: LanguageRow[] = []
  @state() private priorities: PriorityRow[] = []
  @state() private lgfpList: LGFPRow[] = []
  @state() private lgpList: LGPRow[] = []
  @state() private grammaticalFeatures: GrammaticalFeatureRow[] = []
  @state() private grammemes: GrammemeRow[] = []
  @state() private selectedLanguageId = ''
  @state() private loading = true
  /** Pending priority id per LGFP row id (until Update is clicked) */
  @state() private lgfpDraftPriority: Record<string, string> = {}
  /** Pending priority id per LGP row id (until Update is clicked) */
  @state() private lgpDraftPriority: Record<string, string> = {}
  @state() private updatingLgfpId = ''
  @state() private updatingLgpId = ''
  @state() private error = ''
  @state() private success = ''
  @state() private deletingLgfpId = ''
  @state() private restoringLgfpId = ''
  @state() private deletingLgpId = ''
  @state() private restoringLgpId = ''
  @state() private newLgfpFeatureId = ''
  @state() private newLgfpPriorityId = ''
  @state() private newLgpGrammemeId = ''
  @state() private newLgpPriorityId = ''

  connectedCallback() {
    super.connectedCallback()
    const params = new URLSearchParams(window.location.search)
    const id = params.get('languageId') || this.initialLanguageId || ''
    this.selectedLanguageId = id
    this.loadAll()
  }

  private async loadAll() {
    this.loading = true
    this.error = ''
    try {
      const [langRes, prioRes, lgfpRes, lgpRes, gfRes, gramRes] = await Promise.all([
        apolloClient.query({ query: LANGUAGES_QUERY }),
        apolloClient.query({ query: PRIORITIES_QUERY }),
        apolloClient.query({ query: LGFP_QUERY }),
        apolloClient.query({ query: LGP_QUERY }),
        apolloClient.query({ query: GRAMMATICAL_FEATURES_QUERY }),
        apolloClient.query({ query: GRAMMEMES_QUERY }),
      ])
      this.languages = (langRes.data as any).languages ?? []
      this.priorities = (prioRes.data as any).priorities ?? []
      this.lgfpList = (lgfpRes.data as any).languageGrammaticalFeaturePriorities ?? []
      this.lgpList = (lgpRes.data as any).languageGrammemePriorities ?? []
      this.grammaticalFeatures = (gfRes.data as any).grammaticalFeatures ?? []
      this.grammemes = (gramRes.data as any).grammemes ?? []
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.loading = false
    }
  }

  /** Refetch only LGFP and LGP lists (no loading spinner). Use after add/update. */
  private async refreshPriorityLists() {
    try {
      const [lgfpRes, lgpRes] = await Promise.all([
        apolloClient.query({ query: LGFP_QUERY, fetchPolicy: 'network-only' }),
        apolloClient.query({ query: LGP_QUERY, fetchPolicy: 'network-only' }),
      ])
      // Update state with new arrays - create completely new references
      const allLgfp = (lgfpRes.data as any).languageGrammaticalFeaturePriorities ?? []
      const allLgp = (lgpRes.data as any).languageGrammemePriorities ?? []
      // Assign new arrays to trigger LitElement reactivity
      this.lgfpList = allLgfp.slice()
      this.lgpList = allLgp.slice()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    }
  }

  private get lgfpForLanguage(): LGFPRow[] {
    if (!this.selectedLanguageId) return []
    return this.lgfpList.filter((r) => r.language?.id === this.selectedLanguageId)
  }

  /** LGFP rows for the selected language that are not deleted (for add-form and duplicate checks) */
  private get lgfpForLanguageActive(): LGFPRow[] {
    return this.lgfpForLanguage.filter((r) => !r.deletedAt)
  }

  private get lgpForLanguage(): LGPRow[] {
    if (!this.selectedLanguageId) return []
    return this.lgpList.filter((r) => r.language?.id === this.selectedLanguageId)
  }

  /** LGP rows for the selected language that are not deleted (for add-form and duplicate checks) */
  private get lgpForLanguageActive(): LGPRow[] {
    return this.lgpForLanguage.filter((r) => !r.deletedAt)
  }

  /** Grammemes that have no (non-deleted) language grammeme priority for the selected language (for add-form dropdown) */
  private get grammemesNotInLgp(): GrammemeRow[] {
    if (!this.selectedLanguageId) return []
    const used = new Set(this.lgpForLanguageActive.map((r) => r.grammeme?.id).filter(Boolean))
    return this.grammemes.filter((g) => !used.has(g.id))
  }

  /** Grammatical features that have no (non-deleted) priority assigned for the selected language (for add-form dropdown) */
  private get featuresNotInLgfp(): GrammaticalFeatureRow[] {
    if (!this.selectedLanguageId) return []
    const used = new Set(this.lgfpForLanguageActive.map((r) => r.feature?.id).filter(Boolean))
    return this.grammaticalFeatures.filter((f) => !used.has(f.id))
  }

  /** Get priorities not yet assigned to the selected feature for this language (only non-deleted LGFP) */
  private get prioritiesNotAssignedToFeature(): PriorityRow[] {
    if (!this.newLgfpFeatureId) return this.priorities
    const used = new Set(
      this.lgfpForLanguageActive
        .filter((r) => r.feature?.id === this.newLgfpFeatureId)
        .map((r) => r.priority?.id)
    )
    return this.priorities.filter((p) => !used.has(p.id))
  }

  /** Get priorities not yet assigned to the selected grammeme for this language (only non-deleted LGP) */
  private get prioritiesNotAssignedToGrammeme(): PriorityRow[] {
    if (!this.newLgpGrammemeId) return this.priorities
    const used = new Set(
      this.lgpForLanguageActive
        .filter((r) => r.grammeme?.id === this.newLgpGrammemeId)
        .map((r) => r.priority?.id)
    )
    return this.priorities.filter((p) => !used.has(p.id))
  }

  /** Check if the selected feature-priority combination already exists (non-deleted only) */
  private get featurePriorityExists(): boolean {
    if (!this.newLgfpFeatureId || !this.newLgfpPriorityId) return false
    return this.lgfpForLanguageActive.some(
      (r) => r.feature?.id === this.newLgfpFeatureId && r.priority?.id === this.newLgfpPriorityId
    )
  }

  /** Check if the selected grammeme-priority combination already exists (non-deleted only) */
  private get grammemePriorityExists(): boolean {
    if (!this.newLgpGrammemeId || !this.newLgpPriorityId) return false
    return this.lgpForLanguageActive.some(
      (r) => r.grammeme?.id === this.newLgpGrammemeId && r.priority?.id === this.newLgpPriorityId
    )
  }

  private languageLabel(id: string, fallbackName: string): string {
    const out = t(`language_${id}.name`)
    return out === `language_${id}.name` ? fallbackName : out
  }

  private featureLabel(id: string, fallbackName: string): string {
    const out = t(`grammatical_feature_${id}.name`)
    return out === `grammatical_feature_${id}.name` ? fallbackName : out
  }

  private priorityLabel(id: string, fallbackName: string): string {
    const out = t(`priority_${id}.name`)
    return out === `priority_${id}.name` ? fallbackName : out
  }

  private grammemeLabel(id: string, fallbackValue: string): string {
    const out = t(`grammeme_${id}.value`)
    return out === `grammeme_${id}.value` ? fallbackValue : out
  }

  private setSuccess() {
    this.success = t('editDialog.success')
    this.error = ''
    setTimeout(() => { this.success = '' }, 3000)
  }

  private async handleUpdateLgfpPriority(row: LGFPRow) {
    const priorityId = this.lgfpDraftPriority[row.id] ?? row.priority?.id ?? ''
    if (row.priority?.id === priorityId) return
    this.updatingLgfpId = row.id
    try {
      await apolloClient.mutate({
        mutation: UPDATE_LGFP_MUTATION,
        variables: { input: { id: row.id, priorityId } },
      })
      delete this.lgfpDraftPriority[row.id]
      this.lgfpDraftPriority = { ...this.lgfpDraftPriority }
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingLgfpId = ''
    }
  }

  private async handleRemoveLgfp(id: string) {
    this.deletingLgfpId = id
    try {
      await apolloClient.mutate({
        mutation: DELETE_LGFP_MUTATION,
        variables: { id },
      })
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.deletingLgfpId = ''
    }
  }

  private async handleRestoreLgfp(id: string) {
    this.restoringLgfpId = id
    try {
      await apolloClient.mutate({
        mutation: RESTORE_LGFP_MUTATION,
        variables: { id },
      })
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.restoringLgfpId = ''
    }
  }

  private async handleAddLgfp() {
    if (!this.selectedLanguageId || !this.newLgfpFeatureId || !this.newLgfpPriorityId) return
    try {
      await apolloClient.mutate({
        mutation: CREATE_LGFP_MUTATION,
        variables: {
          input: {
            languageId: this.selectedLanguageId,
            featureId: this.newLgfpFeatureId,
            priorityId: this.newLgfpPriorityId,
          },
        },
      })
      this.newLgfpFeatureId = ''
      this.newLgfpPriorityId = ''
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    }
  }

  private async handleUpdateLgpPriority(row: LGPRow) {
    const priorityId = this.lgpDraftPriority[row.id] ?? row.priority?.id ?? ''
    if (row.priority?.id === priorityId) return
    this.updatingLgpId = row.id
    try {
      await apolloClient.mutate({
        mutation: UPDATE_LGP_MUTATION,
        variables: { input: { id: row.id, priorityId } },
      })
      delete this.lgpDraftPriority[row.id]
      this.lgpDraftPriority = { ...this.lgpDraftPriority }
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.updatingLgpId = ''
    }
  }

  private async handleRemoveLgp(id: string) {
    this.deletingLgpId = id
    try {
      await apolloClient.mutate({
        mutation: DELETE_LGP_MUTATION,
        variables: { id },
      })
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.deletingLgpId = ''
    }
  }

  private async handleRestoreLgp(id: string) {
    this.restoringLgpId = id
    try {
      await apolloClient.mutate({
        mutation: RESTORE_LGP_MUTATION,
        variables: { id },
      })
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    } finally {
      this.restoringLgpId = ''
    }
  }

  private async handleAddLgp() {
    if (!this.selectedLanguageId || !this.newLgpGrammemeId || !this.newLgpPriorityId) return
    try {
      await apolloClient.mutate({
        mutation: CREATE_LGP_MUTATION,
        variables: {
          input: {
            languageId: this.selectedLanguageId,
            grammemeId: this.newLgpGrammemeId,
            priorityId: this.newLgpPriorityId,
          },
        },
      })
      this.newLgpGrammemeId = ''
      this.newLgpPriorityId = ''
      await this.refreshPriorityLists()
      this.setSuccess()
    } catch (err: any) {
      this.error = err.message || t('editDialog.error')
    }
  }

  render() {
    if (this.loading) return html`<div class="page-container"><div class="loading">${t('common.loading')}</div></div>`

    return html`
      <div class="page-container edit-language-page">
        <h1>${t('editLanguage.title')}</h1>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        ${this.success ? html`<div class="success">${this.success}</div>` : ''}

        <div class="language-select-row">
          <md-filled-select
            label=${t('editLanguage.selectLanguage')}
            .value=${this.selectedLanguageId}
            @change=${(e: Event) => {
              const id = (e.target as HTMLSelectElement).value
              this.selectedLanguageId = id
              window.history.replaceState({}, '', id ? `${window.location.pathname}?languageId=${id}` : window.location.pathname)
            }}
          >
            <md-select-option value=""><div slot="headline">—</div></md-select-option>
            ${this.languages.map((l) => html`
              <md-select-option value=${l.id}><div slot="headline">${this.languageLabel(l.id, l.name)}</div></md-select-option>
            `)}
          </md-filled-select>
        </div>

        ${!this.selectedLanguageId ? html`
          <p class="select-hint">${t('editLanguage.selectLanguageHint')}</p>
        ` : html`
          <section class="edit-section">
            <h2 class="section-title">${t('editLanguage.grammaticalFeaturePriorities')}</h2>
            <div class="priority-list">
              ${this.lgfpForLanguage.map((row) => {
                const isDeleted = !!row.deletedAt
                const currentPriorityId = row.priority?.id ?? ''
                const draftPriorityId = this.lgfpDraftPriority[row.id] ?? currentPriorityId
                const hasChange = draftPriorityId !== currentPriorityId
                return html`
                <div class="priority-row ${isDeleted ? 'deleted' : ''}">
                  <span class="label">${this.featureLabel(row.feature?.id ?? '', row.feature?.name ?? row.feature?.id ?? '')}</span>
                  ${isDeleted
                    ? html`<span class="label priority-value">${this.priorityLabel(row.priority?.id ?? '', row.priority?.name ?? '')}</span>
                       <span class="deleted-badge">${t('editLanguage.deleted')}</span>
                       <md-filled-button
                         class="restore-button"
                         ?disabled=${this.restoringLgfpId === row.id}
                         @click=${() => this.handleRestoreLgfp(row.id)}
                       >${t('editLanguage.restore')}</md-filled-button>`
                    : html`
                  <md-filled-select
                    .value=${draftPriorityId}
                    @change=${(e: Event) => {
                      const id = (e.target as HTMLSelectElement).value
                      this.lgfpDraftPriority = { ...this.lgfpDraftPriority, [row.id]: id }
                    }}
                  >
                    ${this.priorities.map((p) => html`
                      <md-select-option value=${p.id}><div slot="headline">${this.priorityLabel(p.id, p.name)}</div></md-select-option>
                    `)}
                  </md-filled-select>
                  <md-filled-button
                    ?disabled=${!hasChange || this.updatingLgfpId === row.id}
                    @click=${() => this.handleUpdateLgfpPriority(row)}
                  >${t('editDialog.update')}</md-filled-button>
                  <md-filled-button
                    ?disabled=${this.deletingLgfpId === row.id}
                    @click=${() => this.handleRemoveLgfp(row.id)}
                  >${t('common.delete')}</md-filled-button>`}
                </div>
              `})}
            </div>
            ${this.featuresNotInLgfp.length > 0 ? html`
            <div class="add-priority-row">
              <md-filled-select
                label=${t('meaningGrammemes.grammaticalFeature')}
                .value=${this.newLgfpFeatureId}
                @change=${(e: Event) => {
                  this.newLgfpFeatureId = (e.target as HTMLSelectElement).value
                  // Clear priority when feature changes to avoid invalid combinations
                  this.newLgfpPriorityId = ''
                }}
              >
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.featuresNotInLgfp.map((f) => html`
                  <md-select-option value=${f.id}><div slot="headline">${this.featureLabel(f.id, f.name)}</div></md-select-option>
                `)}
              </md-filled-select>
              <md-filled-select
                label=${t('editLanguage.priority')}
                .value=${this.newLgfpPriorityId}
                @change=${(e: Event) => { this.newLgfpPriorityId = (e.target as HTMLSelectElement).value }}
              >
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.prioritiesNotAssignedToFeature.map((p) => html`
                  <md-select-option value=${p.id}><div slot="headline">${this.priorityLabel(p.id, p.name)}</div></md-select-option>
                `)}
              </md-filled-select>
              <md-filled-button
                ?disabled=${!this.newLgfpFeatureId || !this.newLgfpPriorityId || this.featurePriorityExists}
                @click=${this.handleAddLgfp}
              >${t('editLanguage.addFeaturePriority')}</md-filled-button>
            </div>
            ` : ''}
          </section>

          <section class="edit-section">
            <h2 class="section-title">${t('editLanguage.grammemePriorities')}</h2>
            <div class="priority-list">
              ${this.lgpForLanguage.map((row) => {
                const isDeleted = !!row.deletedAt
                const currentPriorityId = row.priority?.id ?? ''
                const draftPriorityId = this.lgpDraftPriority[row.id] ?? currentPriorityId
                const hasChange = draftPriorityId !== currentPriorityId
                return html`
                <div class="priority-row ${isDeleted ? 'deleted' : ''}">
                  <span class="label">${this.grammemeLabel(row.grammeme?.id ?? '', row.grammeme?.value ?? '')} (${this.featureLabel(row.grammeme?.feature?.id ?? '', row.grammeme?.feature?.name ?? '')})</span>
                  ${isDeleted
                    ? html`<span class="label priority-value">${this.priorityLabel(row.priority?.id ?? '', row.priority?.name ?? '')}</span>
                       <span class="deleted-badge">${t('editLanguage.deleted')}</span>
                       <md-filled-button
                         class="restore-button"
                         ?disabled=${this.restoringLgpId === row.id}
                         @click=${() => this.handleRestoreLgp(row.id)}
                       >${t('editLanguage.restore')}</md-filled-button>`
                    : html`
                  <md-filled-select
                    .value=${draftPriorityId}
                    @change=${(e: Event) => {
                      const id = (e.target as HTMLSelectElement).value
                      this.lgpDraftPriority = { ...this.lgpDraftPriority, [row.id]: id }
                    }}
                  >
                    ${this.priorities.map((p) => html`
                      <md-select-option value=${p.id}><div slot="headline">${this.priorityLabel(p.id, p.name)}</div></md-select-option>
                    `)}
                  </md-filled-select>
                  <md-filled-button
                    ?disabled=${!hasChange || this.updatingLgpId === row.id}
                    @click=${() => this.handleUpdateLgpPriority(row)}
                  >${t('editDialog.update')}</md-filled-button>
                  <md-filled-button
                    ?disabled=${this.deletingLgpId === row.id}
                    @click=${() => this.handleRemoveLgp(row.id)}
                  >${t('common.delete')}</md-filled-button>`}
                </div>
              `})}
            </div>
            <div class="add-priority-row">
              <md-filled-select
                label=${t('meaningGrammemes.grammeme')}
                .value=${this.newLgpGrammemeId}
                @change=${(e: Event) => {
                  this.newLgpGrammemeId = (e.target as HTMLSelectElement).value
                  // Clear priority when grammeme changes to avoid invalid combinations
                  this.newLgpPriorityId = ''
                }}
              >
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.grammemesNotInLgp.map((g) => html`
                  <md-select-option value=${g.id}><div slot="headline">${this.grammemeLabel(g.id, g.value)} (${this.featureLabel(g.feature?.id ?? '', g.feature?.name ?? '')})</div></md-select-option>
                `)}
              </md-filled-select>
              <md-filled-select
                label=${t('editLanguage.priority')}
                .value=${this.newLgpPriorityId}
                @change=${(e: Event) => { this.newLgpPriorityId = (e.target as HTMLSelectElement).value }}
              >
                <md-select-option value=""><div slot="headline">—</div></md-select-option>
                ${this.prioritiesNotAssignedToGrammeme.map((p) => html`
                  <md-select-option value=${p.id}><div slot="headline">${this.priorityLabel(p.id, p.name)}</div></md-select-option>
                `)}
              </md-filled-select>
              <md-filled-button
                ?disabled=${!this.newLgpGrammemeId || !this.newLgpPriorityId || this.grammemePriorityExists}
                @click=${this.handleAddLgp}
              >${t('editLanguage.addGrammemePriority')}</md-filled-button>
            </div>
          </section>
        `}
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'edit-language-page': EditLanguagePage
  }
}
